import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Stack,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  IconButton,
  Tooltip,
  ButtonGroup,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  FaSitemap,
  FaSearch,
  FaPlus,
  FaMinus,
  FaExpand,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { MdOutlineAccountTree } from "react-icons/md";
import { fetchOrgTree } from "store/features/company/employee/employeeActions";
import {
  selectOrgTree,
  selectOrgTreeLoading,
  selectOrgTreeError,
} from "store/features/company/employee/employeeSelectors";

// Helper to get employee initials
const getInitials = (firstName, lastName) => {
  return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
};

// Helper for dynamic avatar colors
const getAvatarBg = (name = "") => {
  const colors = [
    "#f59e0b", // Amber
    "#10b981", // Emerald
    "#3b82f6", // Blue
    "#8b5cf6", // Violet
    "#ec4899", // Pink
    "#ef4444", // Red
    "#14b8a6", // Teal
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Helper to check if a node or its children contain a search match
const hasSearchMatch = (node, term) => {
  if (!term) return false;
  const fullName = `${node.first_name || ""} ${node.last_name || ""}`.toLowerCase();
  const matchSelf =
    fullName.includes(term.toLowerCase()) ||
    node.employee_code?.toLowerCase().includes(term.toLowerCase()) ||
    node.designation_name?.toLowerCase().includes(term.toLowerCase()) ||
    node.department_name?.toLowerCase().includes(term.toLowerCase());

  if (matchSelf) return true;
  return node.children?.some((child) => hasSearchMatch(child, term)) || false;
};

// Recursive Tree Node Component
const TreeNode = ({ node, searchTerm, containerRef }) => {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const cardRef = useRef(null);

  const fullName = `${node.first_name || ""} ${node.last_name || ""}`;
  const isMatch =
    searchTerm &&
    (fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.employee_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.designation_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.department_name?.toLowerCase().includes(searchTerm.toLowerCase()));

  // Auto-expand if child has a search match
  useEffect(() => {
    if (searchTerm && hasSearchMatch(node, searchTerm)) {
      setCollapsed(false);
    }
  }, [searchTerm, node]);

  // Scroll matched element into view centering it on the canvas
  useEffect(() => {
    if (!isMatch || !cardRef.current || !containerRef.current) return;

    const timer = setTimeout(() => {
      const container = containerRef.current;
      const card = cardRef.current;

      const cardRect = card.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const left =
        container.scrollLeft +
        (cardRect.left - containerRect.left) -
        container.clientWidth / 2 +
        cardRect.width / 2;

      const top =
        container.scrollTop +
        (cardRect.top - containerRect.top) -
        container.clientHeight / 2 +
        cardRect.height / 2;

      container.scrollTo({
        left,
        top,
        behavior: "smooth",
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [isMatch, containerRef]);

  const toggleCollapse = (e) => {
    e.stopPropagation();
    setCollapsed(!collapsed);
  };

  const hasChildren = node.children && node.children.length > 0;

  return (
    <li style={{ display: "inline-block", position: "relative" }}>
      {/* Node Content */}
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        style={{ display: "inline-block", position: "relative", zIndex: 10 }}
      >
        <Paper
          ref={cardRef}
          elevation={isMatch ? 8 : 2}
          sx={{
            p: 2,
            width: "240px",
            borderRadius: "14px",
            textAlign: "left",
            position: "relative",
            background:
              theme.palette.mode === "dark"
                ? "rgba(30, 41, 59, 0.9)"
                : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(8px)",
            border: isMatch
              ? "2px solid #f59e0b"
              : `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
            boxShadow: isMatch
              ? "0 0 15px rgba(245, 158, 11, 0.4)"
              : "0 4px 6px -1px rgba(0,0,0,0.05)",
            transition: "border 0.3s, box-shadow 0.3s",
            "&:hover": {
              borderColor: "#f59e0b",
              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
            },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={node.profile_pic_url}
              sx={{
                width: 48,
                height: 48,
                fontSize: "1.1rem",
                fontWeight: 600,
                bgcolor: getAvatarBg(fullName),
                color: "#ffffff",
                // border: "2px solid #ffffff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {getInitials(node.first_name, node.last_name)}
            </Avatar>
            <Box sx={{ overflow: "hidden", width: "100%" }}>
              <Typography
                variant="subtitle2"
                noWrap
                sx={{
                  fontWeight: 600,
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#1e293b",
                }}
              >
                {fullName}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                display="block"
                sx={{
                  color: "primary.main",
                  fontWeight: 500,
                }}
              >
                {node.designation_name || "Employee"} <br />
                <Box sx={{ color: "#64748b" }}>{node.department_name || "N/A"}</Box>
              </Typography>
              {/* <Typography
                variant="caption"
                noWrap
                display="block"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.7rem",
                }}
              >
                {node.department_name || "N/A"}
              </Typography> */}
            </Box>
          </Stack>

          {/* Subordinates count / collapse indicator */}
          {hasChildren && (
            <IconButton
              size="small"
              onClick={toggleCollapse}
              className="no-drag"
              sx={{
                position: "absolute",
                bottom: "-14px",
                left: "50%",
                transform: "translateX(-50%)",
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
                  }`,
                width: "48px",
                height: "24px",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                  borderColor: "#f59e0b",
                },
                zIndex: 1999,
              }}
            >
              {collapsed ? (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: "bold" }}>
                    {node.children.length}
                  </Typography>
                  <FaChevronDown style={{ fontSize: "8px" }} />
                </Stack>
              ) : (
                <FaChevronUp style={{ fontSize: "8px" }} />
              )}
            </IconButton>
          )}
        </Paper>
      </motion.div>

      {/* Children list */}
      {hasChildren && !collapsed && (
        <ul>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              searchTerm={searchTerm}
              containerRef={containerRef}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const OrgTree = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const treeData = useSelector(selectOrgTree);
  const loading = useSelector(selectOrgTreeLoading);
  const error = useSelector(selectOrgTreeError);

  const [searchTerm, setSearchTerm] = useState("");
  const [zoom, setZoom] = useState(1);

  // Pan and Drag functionality refs
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  useEffect(() => {
    dispatch(fetchOrgTree());
  }, [dispatch]);

  const handleMouseDown = (e) => {
    // Prevent drag trigger when interacting with inputs, buttons or dropdown toggles
    if (e.target.closest(".no-drag") || e.target.closest("button") || e.target.closest("input")) {
      return;
    }
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setStartY(e.pageY - containerRef.current.offsetTop);
    setScrollLeft(containerRef.current.scrollLeft);
    setScrollTop(containerRef.current.scrollTop);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const y = e.pageY - containerRef.current.offsetTop;
    const walkX = (x - startX) * 1.5;
    const walkY = (y - startY) * 1.5;
    containerRef.current.scrollLeft = scrollLeft - walkX;
    containerRef.current.scrollTop = scrollTop - walkY;
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
  };

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 1.5));
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));
  const resetZoom = () => setZoom(1);

  const lineColor = theme.palette.mode === "dark" ? "#334155" : "#cbd5e1";

  return (
    <Box
      sx={{
        margin: { xs: -2, md: -3 }, // Offsets the parent page wrapper padding
        height: { xs: "calc(100vh - 64px)", md: "calc(100vh - 80px)" }, // Fills screen height under Navbar
        position: "relative",
        overflow: "hidden",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
      }}
    >
      {/* Dynamic connector styles based on current theme line color */}
      <style>{`
        .org-tree ul {
          padding-top: 24px;
          position: relative;
          transition: all 0.3s;
          display: flex;
          justify-content: center;
        }
        .org-tree li {
          text-align: center;
          list-style-type: none;
          position: relative;
          padding: 24px 16px 0 16px;
          transition: all 0.3s;
          white-space: nowrap;
        }
        /* Top connectors */
        .org-tree li::before, .org-tree li::after {
          content: '';
          position: absolute;
          top: 0;
          right: 50%;
          border-top: 2px solid ${lineColor};
          width: 50%;
          height: 24px;
        }
        .org-tree li::after {
          right: auto;
          left: 50%;
          border-left: 2px solid ${lineColor};
        }
        /* Remove connectors for single sibling elements */
        .org-tree li:only-child::after, .org-tree li:only-child::before {
          display: none;
        }
        .org-tree li:only-child {
          padding-top: 0;
        }
        /* Clean up borders on ends of hierarchy lists */
        .org-tree li:first-child::before, .org-tree li:last-child::after {
          border: 0 none;
        }
        .org-tree li:last-child::before {
          border-right: 2px solid ${lineColor};
          border-radius: 0 8px 0 0;
        }
        .org-tree li:first-child::after {
          border-radius: 8px 0 0 0;
        }
        /* Downward connectors from parent */
        .org-tree ul ul::before {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          border-left: 2px solid ${lineColor};
          width: 0;
          height: 24px;
        }
      `}</style>

      {/* Floating Header Controls */}
      <Box
        sx={{
          px: 4,
          position: "absolute",
          top: 24,
          left: 24,
          zIndex: 100,
          pointerEvents: "none", // Let drag clicks fall through to canvas
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(245, 158, 11, 0.15)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(245, 158, 11, 0.3)",
              pointerEvents: "auto",
            }}
          >
            <FaSitemap size={24} color="#f59e0b" />
          </Box>
          <Box sx={{ pointerEvents: "auto" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              Organization Tree
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.mode === "dark" ? "#94a3b8" : "#4b5563",
                fontWeight: 500,
                display: "block",
              }}
            >
              Visualize reporting structures dynamically
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Floating Search & Zoom controls */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
          position: "absolute",
          top: 24,
          right: 24,
          zIndex: 100,
        }}
      >
        <TextField
          placeholder="Search by name, designation..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: 240,
            "& .MuiOutlinedInput-root": {
              height: "38px",
              borderRadius: "10px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(30, 41, 59, 0.85)"
                  : "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(8px)",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "rgba(51, 65, 85, 0.5)" : "rgba(203, 213, 225, 0.5)",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaSearch color="#64748b" size={14} />
              </InputAdornment>
            ),
          }}
        />

        <ButtonGroup
          variant="outlined"
          sx={{
            height: "38px",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(30, 41, 59, 0.85)"
                : "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(8px)",
            borderColor: theme.palette.mode === "dark" ? "rgba(51, 65, 85, 0.5)" : "rgba(203, 213, 225, 0.5)",
            "& button": {
              minWidth: "38px",
              borderColor:
                theme.palette.mode === "dark" ? "rgba(51, 65, 85, 0.5)" : "rgba(203, 213, 225, 0.5)",
              color: theme.palette.mode === "dark" ? "#cbd5e1" : "#475569",
              "&:hover": {
                borderColor: "#f59e0b",
                color: "primary.main",
              },
            },
          }}
        >
          <Tooltip title="Zoom In">
            <Button onClick={zoomIn}>
              <FaPlus size={12} />
            </Button>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <Button onClick={zoomOut}>
              <FaMinus size={12} />
            </Button>
          </Tooltip>
          <Tooltip title="Reset Zoom">
            <Button onClick={resetZoom}>
              <FaExpand size={12} />
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Stack>

      {/* Loading state */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: "primary.main" }} />
            <Typography color="textSecondary">Loading hierarchy tree...</Typography>
          </Stack>
        </Box>
      ) : error ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Paper sx={{ p: 4, textAlign: "center", maxWidth: 400 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Failed to load hierarchy
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="contained" color="warning" onClick={() => dispatch(fetchOrgTree())}>
              Retry Fetching
            </Button>
          </Paper>
        </Box>
      ) : treeData.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Paper
            sx={{
              p: 6,
              textAlign: "center",
              maxWidth: 450,
              borderRadius: "16px",
            }}
          >
            <MdOutlineAccountTree size={54} color="#94a3b8" />
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              No reporting structure configured
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              Ensure employees have reporting managers set under User Management to render the
              organization tree.
            </Typography>
          </Paper>
        </Box>
      ) : (
        /* Panning board canvas wrapper */
        <Box
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUpOrLeave}
          onMouseLeave={handleMouseUpOrLeave}
          sx={{
            width: "100%",
            height: "100%",
            overflow: "auto",
            position: "relative",
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
            "&::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.1)",
              borderRadius: "4px",
            },
            overflow: "hidden",
          }}
        >
          {/* Scroll instruction indicator */}
          {/* <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: 16,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(30, 41, 59, 0.85)"
                  : "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(4px)",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(30, 41, 59, 0.5)" : "rgba(203, 213, 225, 0.5)"
                }`,
              borderRadius: "6px",
              px: 1.5,
              py: 0.5,
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
              Hold left click and drag to pan the board
            </Typography>
          </Box> */}

          {/* Hierarchy layout canvas */}
          <Box
            sx={{
              minWidth: "max-content",
              minHeight: "max-content",
              pt: 18, // Extra top padding to make room for absolute-positioned header
              pb: 12,
              px: 8,
              transform: `scale(${zoom})`,
              transformOrigin: "top center",
              transition: "transform 0.2s ease-out",
            }}
          >
            <div className="org-tree">
              <ul>
                {treeData.map((root) => (
                  <TreeNode
                    key={root.id}
                    node={root}
                    searchTerm={searchTerm}
                    containerRef={containerRef}
                  />
                ))}
              </ul>
            </div>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OrgTree;
